/**
 * @file game-commands.ts
 * @description Game action slash commands for the in-game chat.
 *
 * Defines the catalog of game commands, multi-stage completion types,
 * and dynamic command generation from character data.
 */

import type { SlashCommandDefinition } from './chat-commands';
import { buildCardSlashCommand } from './build-card-command';
import { REACTION_SPELLS } from '@/lib/intent/build-intent';
import type {
  ActionCard,
  CharacterPermanent,
  CharacterSession,
  GameCharacterInfo,
  GameSpellcastingInfo,
  SceneItem,
  SceneInteractable,
  TravelDestination,
} from '@danda/shared';
import type {
  DamageType,
  SpellEffect,
  SpellTargetAudience,
} from '@danda/shared/elasticsearch/combat-mechanics';
import {
  PICKUP_ALL_SENTINEL,
  CLOSABLE_INTERACTABLE_TYPES,
  EQUIPPABLE_ITEM_KINDS,
} from '@danda/shared';

// =============================================================================
// Completion Step Types
// =============================================================================

/** What kind of sub-menu to show after selecting a command */
export type CompletionStepType =
  | 'enemies'
  | 'allies'
  /** Alive party members INCLUDING self. Used by healing/buff cards whose SRD
   *  text allows the caster to target themselves (Lay on Hands). Distinct
   *  from `'allies'` (party-only, no self) which backs cards like Bardic
   *  Inspiration where SRD says "another creature other than yourself." */
  | 'allies_or_self'
  | 'dying_allies'
  /** Dead party members (Revivify / Raise Dead). Dead-only: heals/buffs use the
   *  alive pickers, so revive spells route here to reach a fallen ally. */
  | 'dead_allies'
  | 'all_targets'
  /** Self + alive party members + alive friendly/allied scene NPCs.
   *  Used by `/use` for consumables (potions, scrolls) where the player can
   *  apply the item to themselves or any friendly. Distinct from `'allies'`
   *  (party-only, no self) because the party-only step is also wired to
   *  buff cards like Bardic Inspiration where SRD forbids self-target. */
  | 'friendly_targets'
  | 'spells'
  | 'inventory_items'
  | 'usable_items'
  | 'equippable_items'
  | 'scene_items'
  | 'scene_objects'
  | 'openable_objects'
  | 'closable_objects'
  | 'breakable_objects'
  | 'scene_chars'
  | 'scene_npcs'
  | 'party_members'
  | 'item_spells'
  | 'item_abilities'
  | 'travel_destinations'
  /** Amount selector for pool-spend cards (Paladin Lay on Hands). Renders a
   *  discrete menu of HP amounts (1, 2, 3, 5, 10, pool max). Selection is
   *  stored as meta.poolSpend and fed to `UseAbilityAction.pool_spend`. */
  | 'pool_spend'
  /** Spell slot level picker — shows each level the caster has at least one
   *  remaining slot for. Used by /font-of-inspiration (Bard L5+, expend slot
   *  to regain Bardic Inspiration use). Selection is stored as
   *  meta.slotLevel and fed to `IntentFontOfInspiration.slot_level`. */
  | 'spell_slot_levels'
  /** Damage-type picker for Sorcerer Transmuted Spell metamagic. SRD 5.2:
   *  caster may swap a spell's damage type to one of Acid / Cold / Fire /
   *  Lightning / Poison / Thunder. Six fixed options. Selection stored as
   *  meta.transmutedDamageType and threaded into `IntentCastSpell` via
   *  `mergeCardIntent`. */
  | 'transmuted_damage_type'
  /** Upcast slot picker for `/cast` — runs between the spell pick and the
   *  target pick. Lists each slot level the caster has remaining starting
   *  at the spell's base level. The base level renders first so the
   *  default is "cast at base." Skipped automatically (single option →
   *  auto-pick) for cantrips, free-cast spells, and leveled spells whose
   *  caster only has slots at the base level. Selection lives in
   *  `meta.slotLevel` and overrides `acc.spellLevel` (which seeds the
   *  spell's base level from the `spells` step). */
  | 'slot_level_choice'
  /** Sorcerer **Twinned Spell** second-target picker. Runs after the
   *  primary target step on a `spell_cast_with_metamagic` card whose
   *  `option === 'twinned_spell'`. Builder returns the same roster as
   *  the spell's first-target step (minus the picked primary). When the
   *  picked spell isn't `twinnable`, the builder returns `[]` so the
   *  unified empty-state path dispatches the bubble "Can't twin this
   *  spell." and aborts. Selection lands on `acc.twinnedTarget` and
   *  fills `IntentCastSpell.twinned_target`. */
  | 'twinned_target'
  /** Sorcerer **Careful Spell** exclude-list picker. Multi-select sheet
   *  of party allies caught in the spell's AoE that the caster wants to
   *  exclude. Cap is `min(CHA mod, party allies in radius)`. Builder
   *  returns `[]` when no allies would be caught (auto-skip). Selection
   *  lands on `acc.carefulExcludeTargetIds` and fills
   *  `IntentCastSpell.careful_exclude_targets`. */
  | 'careful_exclude'
  /** Bestow Curse caster-pick (SRD 5.2 L3 Necromancy). Four mutually-
   *  exclusive variants: `AbilityDisadvantage`, `AttacksVsCasterDisadvantage`,
   *  `ForceDodgeOnFailedWis`, `CasterDealsExtraNecrotic`. Builder returns
   *  one item per variant; the source-id matches the Rust enum's variant
   *  name. When the player picks `AbilityDisadvantage`, the completion
   *  chain continues to `ability_picker` for the sub-pick (which of the
   *  six abilities to disadvantage). Selection lands on
   *  `acc.bestowCurseEffect` and fills `IntentCastSpell.bestow_curse_effect`. */
  | 'bestow_curse_effect'
  /** Generic ability sub-picker — six items (strength, dexterity, constitution,
   *  intelligence, wisdom, charisma) used by the Bestow Curse
   *  `AbilityDisadvantage` branch. The picked source-id is the canonical
   *  lower-case ability key the backend's `Ability` enum deserializes from. */
  | 'ability_picker'
  /** Concentration swap confirm — runs in `/cast` between the spell pick and
   *  the target pick when the caster is already concentrating on a different
   *  spell. Two options: "Continue (end {currentConc})" and "Cancel". The
   *  cancel option carries `meta.cancelToSpellPick: true` so the wizard
   *  unwinds to the spell-pick step. Skipped (via `shouldSkipNextStep`) when
   *  the player isn't concentrating or the picked spell doesn't require
   *  concentration. The picked spell carries `meta.concentrationToReplace`
   *  with the current concentration name when applicable; the predicate
   *  reads that to decide whether to render. */
  | 'concentration_confirm';

// =============================================================================
// Game Command Definition (extends SlashCommandDefinition)
// =============================================================================

export interface GameSlashCommand extends SlashCommandDefinition {
  intentType: string;
  category: 'combat' | 'interaction' | 'party' | 'communication' | 'equipment';
  /** Game modes where this command is available (undefined = all) */
  gameModes?: ('combat' | 'exploration' | 'social')[];
  /** Ordered list of sub-menu steps after the command is selected */
  completionSteps?: CompletionStepType[];
  /** Template for building fill text. Use {spell}, {target}, {item}, {object} placeholders */
  textTemplate?: string;
  /** Whether the final step adds free text (e.g., /whisper → target → free text).
   *  When `true` and the user types a body beyond the canonical template,
   *  `finalizeForSubmit` returns `intent: null` so the message ships through
   *  `player_text` (LLM agent path). Pure menu picks still ship typed Intents. */
  endsWithFreeText?: boolean;
  /** For ProposeDecision commands */
  decisionType?: string;
  /** For Vote commands */
  vote?: boolean;
}

// =============================================================================
// Static Game Commands
// =============================================================================

export const GAME_COMMANDS: GameSlashCommand[] = [
  // ---- Utility (intercepted by ChatPanel before the intent pipeline) ----
  {
    name: 'feedback',
    emoji: '💬',
    description: 'Send feedback to the team',
    // Keeps the slash prefix in the textarea so parseSlashCommand catches it
    // in ChatPanel before any game dispatch runs. intentType is required by
    // GameSlashCommand but is never actually submitted — the slash-command
    // interceptor short-circuits first.
    fillText: '/feedback ',
    intentType: 'feedback',
    category: 'communication',
    roomTypes: ['game'],
  },
  // ---- Combat Actions ----
  {
    // Available in every mode so a player who sees a hostile in exploration
    // (or wants to break a hostile mid-conversation in social) can trigger
    // combat via the slash menu. With `combat`-only gating, the slash falls
    // back to plain chat — the enricher then has to recover an Attack intent
    // from natural-language text without a canonical target_id, which fails
    // often enough that players assume `/attack` is broken.
    name: 'attack',
    description: 'Attack a target',
    intentType: 'attack',
    category: 'combat',
    completionSteps: ['enemies'],
    textTemplate: 'Attack {target}',
    roomTypes: ['game'],
  },
  {
    name: 'cast',
    description: 'Cast a spell',
    intentType: 'cast_spell',
    category: 'combat',
    // `bestow_curse_effect` is conditionally skipped via `shouldSkipNextStep`
    // when the selected spell isn't Bestow Curse. `ability_picker` is the
    // AbilityDisadvantage sub-pick — also skipped unless the user chose
    // AbilityDisadvantage at bestow_curse_effect. Without these steps in
    // the chain, `/cast Bestow Curse` would silently default to
    // `CasterDealsExtraNecrotic` (the resolver's None-branch fallback).
    //
    // `concentration_confirm` sits immediately after `spells` so the skip
    // predicate sees the spells selection (which carries
    // `meta.concentrationToReplace`) as its `previousItem`. If it were
    // placed AFTER bestow_curse_effect/ability_picker, the predicate would
    // see the curse-effect/ability item instead and silently skip the
    // swap-confirm dialog.
    completionSteps: [
      'spells',
      'concentration_confirm',
      'bestow_curse_effect',
      'ability_picker',
      'slot_level_choice',
      'all_targets',
    ],
    textTemplate: 'Cast {spell} at {target}',
    roomTypes: ['game'],
  },
  {
    name: 'dodge',
    description: 'Take the Dodge action',
    fillText: 'Take the Dodge action',
    intentType: 'dodge',
    category: 'combat',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  {
    name: 'dash',
    description: 'Take the Dash action',
    fillText: 'Dash',
    intentType: 'dash',
    category: 'combat',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  {
    name: 'disengage',
    description: 'Take the Disengage action',
    fillText: 'Disengage',
    intentType: 'disengage',
    category: 'combat',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  {
    name: 'hide',
    description: 'Take the Hide action',
    fillText: 'Hide',
    intentType: 'hide',
    category: 'combat',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  {
    name: 'help',
    description: 'Help a creature',
    intentType: 'help',
    category: 'combat',
    gameModes: ['combat'],
    completionSteps: ['all_targets'],
    textTemplate: 'Help {target}',
    roomTypes: ['game'],
  },
  {
    name: 'stabilize',
    description: 'Stabilize a dying ally',
    intentType: 'stabilize',
    category: 'combat',
    gameModes: ['combat'],
    completionSteps: ['dying_allies'],
    textTemplate: 'Stabilize {target}',
    roomTypes: ['game'],
  },
  {
    name: 'escape',
    description: 'Escape a grapple',
    fillText: 'Try to escape the grapple',
    intentType: 'escape_grapple',
    category: 'combat',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  {
    // Free action (house rule, per WakeUpHandler in stance.rs) — shake/shout
    // a sleeping ally awake. Engine validates that the target has a wakeable
    // Unconscious condition (Sleep spell, etc.); other targets get a clean
    // "is not in a wakeable sleep" rejection. Available outside combat too.
    name: 'wakeup',
    description: 'Wake a sleeping ally',
    intentType: 'wake_up',
    category: 'combat',
    completionSteps: ['allies'],
    textTemplate: 'Wake {target}',
    roomTypes: ['game'],
  },
  {
    name: 'prone',
    description: 'Drop prone',
    fillText: 'Drop prone',
    intentType: 'drop_prone',
    category: 'combat',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  {
    name: 'standup',
    description: 'Stand up from prone',
    fillText: 'Stand up',
    intentType: 'stand_up',
    category: 'combat',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  {
    // Bard L5+ feature — expend a spell slot to regain one Bardic
    // Inspiration use. Free action per SRD 5.2 ("no action required") so it
    // works in combat AND exploration. Engine handler validates Bard L5+,
    // requires at least one expended BI use, and verifies slot availability;
    // non-Bards / unprepared casters get a clean rejection message.
    name: 'font-of-inspiration',
    description: 'Expend a spell slot to regain a Bardic Inspiration use (Bard L5+)',
    intentType: 'font_of_inspiration',
    category: 'combat',
    completionSteps: ['spell_slot_levels'],
    textTemplate: 'Use Font of Inspiration: expend a level {level} slot',
    roomTypes: ['game'],
  },
  {
    // Concentration outlasts combat — Bless cast pre-fight that the player
    // wants to drop before exiting the encounter, or a buff lingering into
    // the next scene. Combat-only gating made `/end-conc` invisible in
    // exploration even when the character was actively concentrating.
    name: 'end-conc',
    description: 'End concentration on a spell',
    fillText: 'End concentration',
    intentType: 'end_concentration',
    category: 'combat',
    roomTypes: ['game'],
  },

  // ---- Interaction ----
  {
    name: 'search',
    description: 'Search the area',
    fillText: 'Search the area',
    intentType: 'search',
    category: 'interaction',
    roomTypes: ['game'],
  },
  {
    name: 'investigate',
    description: 'Investigate something',
    intentType: 'investigate',
    category: 'interaction',
    completionSteps: ['scene_objects'],
    textTemplate: 'Investigate {object}',
    roomTypes: ['game'],
    // Object pick is the last REQUIRED argument — auto-submit on selection.
    // Free-text suffixes (`/investigate chest for hidden compartments`) are
    // OPTIONAL flavor; users who want to add them type the full text before
    // pressing Enter (finalizeForSubmit handles the player_text routing then).
    // `endsWithFreeText` is only set when the body is REQUIRED (say, sayto,
    // whisper, shout — where the body IS the content).
  },

  {
    name: 'use',
    description: 'Use an item',
    intentType: 'use_item',
    category: 'interaction',
    completionSteps: ['usable_items', 'friendly_targets'],
    textTemplate: 'Use {item} on {target}',
    roomTypes: ['game'],
  },
  {
    // `/pickup <item>` picks one. `/pickup all` (or `/pickup everything`)
    // routes through `resolveFromSubmit` to the `__all__` sentinel that the
    // backend `PickUpHandler` expands into one `pick_up` per lootable scene
    // item (corpses are `lootable: false`, naturally excluded).
    name: 'pickup',
    description: 'Pick up an item (use "all" to grab every lootable)',
    intentType: 'pick_up',
    category: 'interaction',
    completionSteps: ['scene_items'],
    textTemplate: 'Pick up {item}',
    roomTypes: ['game'],
  },
  {
    name: 'drop',
    description: 'Drop an item',
    intentType: 'drop',
    category: 'interaction',
    completionSteps: ['inventory_items'],
    textTemplate: 'Drop {item}',
    roomTypes: ['game'],
    // Wondrous items (rings, amulets, robes, etc.) are deliberately not
    // droppable from the slash picker — they're either quest-critical
    // artifacts (Amulet of Theron) or attuned magic with no in-fiction
    // reason to fling on the ground mid-adventure. Player can still
    // unequip, then keep the item in inventory.
    meta: { excludeWondrous: true },
  },
  {
    name: 'open',
    description: 'Open something',
    intentType: 'open',
    category: 'interaction',
    completionSteps: ['openable_objects'],
    textTemplate: 'Open {object}',
    roomTypes: ['game'],
  },
  {
    name: 'close',
    description: 'Close something',
    intentType: 'close',
    category: 'interaction',
    completionSteps: ['closable_objects'],
    textTemplate: 'Close {object}',
    roomTypes: ['game'],
  },
  {
    name: 'break',
    description: 'Smash a breakable object',
    intentType: 'interact',
    category: 'interaction',
    completionSteps: ['breakable_objects'],
    textTemplate: 'Break the {object}',
    roomTypes: ['game'],
  },

  // ---- Party Decisions ----
  // Both rest commands are reachable from BOTH the in-game roleplay room
  // (`game`) AND the party chat tab (`party`) inside a live session, since
  // rest is a party-coordination action that players often discuss in the
  // party chat before triggering. Same wire intent + handler in both rooms.
  {
    name: 'rest',
    description: 'Propose a short rest',
    fillText: 'Propose a short rest',
    intentType: 'propose_decision',
    decisionType: 'short_rest',
    category: 'party',
    gameModes: ['exploration', 'social'],
    roomTypes: ['game', 'party'],
  },
  {
    name: 'long-rest',
    description: 'Propose a long rest',
    fillText: 'Propose a long rest',
    intentType: 'propose_decision',
    decisionType: 'long_rest',
    category: 'party',
    gameModes: ['exploration', 'social'],
    roomTypes: ['game', 'party'],
  },
  {
    name: 'invite',
    description: 'Invite an NPC to join your party',
    intentType: 'propose_decision',
    decisionType: 'invite_npc',
    category: 'party',
    completionSteps: ['scene_npcs'],
    textTemplate: 'Invite {target} to join the party',
    gameModes: ['exploration', 'social'],
    roomTypes: ['game'],
  },
  {
    name: 'flee',
    description: 'Flee from combat',
    fillText: 'Flee!',
    intentType: 'flee',
    category: 'party',
    gameModes: ['combat'],
    roomTypes: ['game'],
  },
  // /yes and /no were the party-vote acceptance buttons. The party-vote
  // system has been retired: `/rest`, `/long-rest`, `/travel`, and
  // `/end-session` now auto-accept on the leader's submission (it was too
  // cumbersome). Until / unless party voting comes back, these slashes are
  // dead UI — easy to type accidentally, ship a `Vote` intent that lands
  // with no active proposal. Verified absent from in-game flow 2026-05-31.

  // ---- Communication ----
  {
    name: 'say',
    description: 'Say something in character',
    fillText: 'Say: ',
    intentType: 'say',
    category: 'communication',
    // Typed body becomes the message. `finalizeForSubmit` extracts the body
    // after the canonical "Say: " prefix and builds `Intent::Say` directly
    // via `buildCommunicationIntent` — no intent converter LLM round-trip.
    endsWithFreeText: true,
    roomTypes: ['game'],
  },
  {
    name: 'sayto',
    description: 'Say something to a specific character',
    intentType: 'talk_to',
    category: 'communication',
    completionSteps: ['scene_chars'],
    textTemplate: 'Say to {target}: ',
    endsWithFreeText: true,
    roomTypes: ['game'],
  },
  {
    name: 'whisper',
    description: 'Whisper to a character',
    intentType: 'whisper',
    category: 'communication',
    completionSteps: ['scene_chars'],
    textTemplate: 'Whisper to {target}: ',
    endsWithFreeText: true,
    roomTypes: ['game'],
  },
  {
    // Direct action — no leading "I". Submitted text is "Shout: <msg>".
    // Both pure menu picks (zero-step `/shout` then send) and bodies typed
    // after the canonical "Shout: " prefix get `Intent::Shout` built
    // directly via the comm finalizer — no intent converter LLM round-trip.
    name: 'shout',
    description: 'Shout something',
    fillText: 'Shout: ',
    intentType: 'shout',
    category: 'communication',
    endsWithFreeText: true,
    roomTypes: ['game'],
  },

  // ---- Equipment (non-combat) ----
  {
    name: 'equip',
    description: 'Equip an item',
    intentType: 'equip',
    category: 'equipment',
    gameModes: ['exploration', 'social'],
    completionSteps: ['equippable_items'],
    textTemplate: 'Equip {item}',
    roomTypes: ['game'],
  },
  {
    name: 'give',
    description: 'Give an item to someone',
    intentType: 'give',
    category: 'equipment',
    completionSteps: ['inventory_items', 'party_members'],
    textTemplate: 'Give {item} to {target}',
    roomTypes: ['game'],
    // Filter quest-critical items (Amulet of Theron, etc.) AND wondrous
    // items (rings, amulets, robes, …) from the recipient picker.
    // Wondrous magic is either quest-critical or attuned to the wearer —
    // never something a player meant to hand to a party member. `/equip`
    // uses the same step type but IS allowed to see both (the Amulet
    // must be wearable to grant Dragon Ward; the player must be able to
    // attune their own magic). `/drop` reuses `excludeWondrous` too.
    meta: { excludeQuestItems: true, excludeWondrous: true },
  },
  {
    name: 'givecoins',
    description: 'Give gold to someone (e.g. "50 gp" or "500 cp")',
    intentType: 'give_coins',
    category: 'equipment',
    completionSteps: ['party_members'],
    textTemplate: 'Give gold to {target}: ',
    // Body (amount, e.g. "50 gp") is REQUIRED — without it the intent has
    // nothing to transfer. Picker pick alone must NOT auto-submit; the user
    // types the amount before pressing Enter.
    endsWithFreeText: true,
    roomTypes: ['game'],
  },

  // ---- Bottom slots: travel + end-session pinned at the menu tail so the
  // most consequential exits stay easy to find at the bottom of the list.
  {
    name: 'travel',
    description: 'Travel to a destination',
    intentType: 'travel',
    category: 'interaction',
    completionSteps: ['travel_destinations'],
    textTemplate: 'Travel to {destination}',
    gameModes: ['exploration', 'social'],
    roomTypes: ['game'],
  },
  {
    name: 'end-session',
    description: 'End the game session.',
    fillText: 'End the game session',
    intentType: 'propose_decision',
    decisionType: 'end_session',
    category: 'party',
    roomTypes: ['game'],
  },
];

// =============================================================================
// Menu Option Builders (generate SlashCommandDefinition[] for sub-menus)
// =============================================================================

function healthLabel(char: GameCharacterInfo): string {
  const state: string = char.healthState ?? 'uninjured';
  const parts = [state];
  if (char.conditions?.length) {
    const names = char.conditions.map((c) => (typeof c === 'string' ? c : c.name));
    parts.push(names.join(', '));
  }
  return parts.join(' · ');
}

export function buildEnemyOptions(
  sceneChars: GameCharacterInfo[],
  stageLabel?: string
): SlashCommandDefinition[] {
  // Hostile-only: monsters by type, or NPCs with `hostile` / `unfriendly`
  // attitude. Mirrors the symmetric `buildFriendlyTargetOptions` exclusion
  // and the mobile `buildEnemyOptions` filter so an `unfriendly` NPC (a
  // hostile-leaning faction member, brigand, etc.) stays reachable as a
  // weapon/spell target on both platforms. Friendly/neutral NPCs (Bram
  // the shepherd, merchants) are excluded so the single-option auto-pick
  // can't silently resolve to them. Explicit friendly-fire still works via
  // a typed `/attack <name>` per the friendly_fire_player_agency carve-out.
  const enemies = sceneChars.filter(
    (c) =>
      c.isPartyMember !== true &&
      // Drop the defeated: `dead`, and also `dying` (0-HP / downed). A monster
      // reads `dying` for a beat before flipping to `dead`, and a 0-HP foe is
      // never a valid attack/hostile-spell target. Matches the engine NPC-AI
      // treatment of Dying|Dead as out of action.
      c.healthState !== 'dead' &&
      c.healthState !== 'dying' &&
      (c.type === 'monster' || c.attitude === 'hostile' || c.attitude === 'unfriendly')
  );
  if (enemies.length === 0) return [];
  return enemies.map((c) => ({
    name: c.name,
    description: healthLabel(c),
    isMenuOption: true,
    sourceId: c.charId,
    stageLabel,
    emoji: c.avatarEmoji,
    avatarUrl: c.avatarUrl ?? c.portraitUrl,
    meta: { healthState: c.healthState },
  }));
}

export function buildAllyOptions(
  partyMembers: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  const allies = partyMembers.filter((c) => c.charId !== playerCharId && c.healthState !== 'dead');
  if (allies.length === 0) return [];
  return allies.map((c) => ({
    name: c.name,
    description: healthLabel(c),
    isMenuOption: true,
    sourceId: c.charId,
    stageLabel,
    emoji: c.avatarEmoji,
    avatarUrl: c.avatarUrl ?? c.portraitUrl,
    meta: { healthState: c.healthState },
  }));
}

/** Like `buildAllyOptions` but includes self as the first entry. Backs the
 *  `'allies_or_self'` completion step for healing/buff abilities whose SRD
 *  text allows self-target (Lay on Hands: "touch a creature (which could be
 *  yourself)"). Self renders as "Self · You · <health>" so it's distinct
 *  from named party members at a glance. */
export function buildAllyOrSelfOptions(
  partyMembers: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  const selfChar =
    playerCharId != null
      ? partyMembers.find((c) => c.charId === playerCharId && c.healthState !== 'dead')
      : undefined;
  const others = partyMembers.filter((c) => c.charId !== playerCharId && c.healthState !== 'dead');
  if (!selfChar && others.length === 0) return [];
  const options: SlashCommandDefinition[] = [];
  if (selfChar) {
    options.push({
      name: 'Self',
      description: `You · ${healthLabel(selfChar)}`,
      isMenuOption: true,
      sourceId: selfChar.charId,
      stageLabel,
      emoji: selfChar.avatarEmoji,
      avatarUrl: selfChar.avatarUrl ?? selfChar.portraitUrl,
      meta: { healthState: selfChar.healthState },
    });
  }
  options.push(
    ...others.map((c) => ({
      name: c.name,
      description: healthLabel(c),
      isMenuOption: true,
      sourceId: c.charId,
      stageLabel,
      emoji: c.avatarEmoji,
      avatarUrl: c.avatarUrl ?? c.portraitUrl,
      meta: { healthState: c.healthState },
    }))
  );
  return options;
}

export function buildDyingAllyOptions(
  partyMembers: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  const dying = partyMembers.filter((c) => c.charId !== playerCharId && c.healthState === 'dying');
  if (dying.length === 0) return [];
  return dying.map((c) => ({
    name: c.name,
    description: 'Dying',
    isMenuOption: true,
    sourceId: c.charId,
    stageLabel,
    emoji: c.avatarEmoji,
    avatarUrl: c.avatarUrl ?? c.portraitUrl,
  }));
}

/** Spells that restore a DEAD creature to life. Routed to `dead_allies` (a
 *  dead-only ally picker) instead of the audience-derived friendly/all-target
 *  pickers, which exclude the dead. Mirrors mobile `_kReviveSpells`. */
export const REVIVE_SPELLS = new Set<string>([
  'revivify',
  'raise dead',
  'resurrection',
  'true resurrection',
]);

export function isReviveSpell(name: string): boolean {
  return REVIVE_SPELLS.has(name.toLowerCase());
}

/** Dead party allies excluding self. Backs revive spells (Revivify / Raise
 *  Dead) — the dead-only counterpart to `buildAllyOrSelfOptions` so a fallen
 *  ally is reachable while heals/buffs/Help keep excluding the dead. Web
 *  backfills `healthState` at ingestion, so `=== 'dead'` is reliable. */
export function buildDeadAllyOptions(
  partyMembers: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  const dead = partyMembers.filter((c) => c.charId !== playerCharId && c.healthState === 'dead');
  if (dead.length === 0) return [];
  return dead.map((c) => ({
    name: c.name,
    description: 'Fallen',
    isMenuOption: true,
    sourceId: c.charId,
    stageLabel,
    emoji: c.avatarEmoji,
    avatarUrl: c.avatarUrl ?? c.portraitUrl,
  }));
}

export function buildAllTargetOptions(
  partyMembers: GameCharacterInfo[],
  sceneChars: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  // Merge party + scene chars, dedup by charId (party NPCs may appear in both)
  const seen = new Set<string>();
  const targets: GameCharacterInfo[] = [];

  // Add "Self" as the first target option (for self-targeting spells like Mage Armor, Shield)
  const selfChar = playerCharId
    ? partyMembers.find((c) => c.charId === playerCharId && c.healthState !== 'dead')
    : undefined;

  for (const c of [
    ...partyMembers.filter((c) => c.charId !== playerCharId && c.healthState !== 'dead'),
    // Drop defeated ENEMIES (dead OR dying) the same way buildEnemyOptions does —
    // a downed foe is not a valid target. Keep dying ALLIES (party members in the
    // scene) so heal / Revivify can still target them.
    ...sceneChars.filter(
      (c) => c.healthState !== 'dead' && !(c.isPartyMember !== true && c.healthState === 'dying')
    ),
  ]) {
    if (!seen.has(c.charId)) {
      seen.add(c.charId);
      targets.push(c);
    }
  }
  if (!selfChar && targets.length === 0) return [];
  const options: SlashCommandDefinition[] = [];
  if (selfChar) {
    options.push({
      name: 'Self',
      description: `You · ${healthLabel(selfChar)}`,
      isMenuOption: true,
      sourceId: selfChar.charId,
      stageLabel,
      emoji: selfChar.avatarEmoji,
      avatarUrl: selfChar.avatarUrl ?? selfChar.portraitUrl,
      meta: { healthState: selfChar.healthState, isAlly: true },
    });
  }
  options.push(
    ...targets.map((c) => ({
      name: c.name,
      description: `${c.isPartyMember ? 'Ally' : 'Enemy'} · ${healthLabel(c)}`,
      isMenuOption: true,
      sourceId: c.charId,
      stageLabel,
      emoji: c.avatarEmoji,
      avatarUrl: c.avatarUrl ?? c.portraitUrl,
      meta: { healthState: c.healthState, isAlly: c.isPartyMember },
    }))
  );
  return options;
}

/** Self + alive party members + alive non-hostile scene NPCs.
 *  Drives the `'friendly_targets'` step used by `/use` for consumables AND
 *  the runtime-substituted `'friendly'`-audience spell target step (Bless,
 *  Cure Wounds): the player can buff/heal themselves, a party member, or any
 *  non-hostile NPC in the scene. Only `hostile`/`unfriendly` scene NPCs are
 *  excluded so the boss never shows as a buff target; `neutral` / `friendly`
 *  / `allied` / undefined-attitude NPCs (a townsfolk, a captive) all qualify.
 *  Monsters (`isPartyMember !== true` with a hostile/unfriendly attitude) are
 *  filtered out by the attitude gate. */
export function buildFriendlyTargetOptions(
  partyMembers: GameCharacterInfo[],
  sceneChars: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  const seen = new Set<string>();
  const selfChar = playerCharId
    ? partyMembers.find((c) => c.charId === playerCharId && c.healthState !== 'dead')
    : undefined;

  const targets: GameCharacterInfo[] = [];
  // Mirror `buildAllTargetOptions` dedup pattern: a party NPC may surface in
  // both arrays. Pull party from `partyMembers` (authoritative), then any
  // non-hostile scene char (including any party-marked entry that's only in
  // `sceneChars`). Undefined attitude is treated as included — a freshly
  // spawned neutral NPC shouldn't vanish from the heal list. The Set drops
  // duplicates.
  for (const c of [
    ...partyMembers.filter((c) => c.charId !== playerCharId && c.healthState !== 'dead'),
    ...sceneChars.filter(
      (c) =>
        c.healthState !== 'dead' &&
        c.charId !== playerCharId &&
        (c.isPartyMember === true ||
          (c.type !== 'monster' && c.attitude !== 'hostile' && c.attitude !== 'unfriendly'))
    ),
  ]) {
    if (!seen.has(c.charId)) {
      seen.add(c.charId);
      targets.push(c);
    }
  }

  if (!selfChar && targets.length === 0) return [];
  const options: SlashCommandDefinition[] = [];
  if (selfChar) {
    options.push({
      name: 'Self',
      description: `You · ${healthLabel(selfChar)}`,
      isMenuOption: true,
      sourceId: selfChar.charId,
      stageLabel,
      emoji: selfChar.avatarEmoji,
      avatarUrl: selfChar.avatarUrl ?? selfChar.portraitUrl,
      meta: { healthState: selfChar.healthState, isAlly: true },
    });
  }
  options.push(
    ...targets.map((c) => ({
      name: c.name,
      description: `${c.isPartyMember ? 'Ally' : 'Friendly'} · ${healthLabel(c)}`,
      isMenuOption: true,
      sourceId: c.charId,
      stageLabel,
      emoji: c.avatarEmoji,
      avatarUrl: c.avatarUrl ?? c.portraitUrl,
      meta: { healthState: c.healthState, isAlly: true },
    }))
  );
  return options;
}

/** The single "Self" row from `buildAllTargetOptions`, returned as a 1-element
 *  array (or `[]` when the caster isn't a live party member). Backs the
 *  runtime substitution for `self_only`-audience spells (Mage Armor, Shield,
 *  Misty Step): a lone option auto-commits in the wizard without ever opening
 *  a target sheet — exactly the self-cast UX we want. */
export function buildSelfOnlyTargetOption(
  partyMembers: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  const selfChar = playerCharId
    ? partyMembers.find((c) => c.charId === playerCharId && c.healthState !== 'dead')
    : undefined;
  if (!selfChar) return [];
  return [
    {
      name: 'Self',
      description: `You · ${healthLabel(selfChar)}`,
      isMenuOption: true,
      sourceId: selfChar.charId,
      stageLabel,
      emoji: selfChar.avatarEmoji,
      avatarUrl: selfChar.avatarUrl ?? selfChar.portraitUrl,
      // `audienceSelfOnly` lets `advanceToStep` auto-commit this lone option
      // even though `all_targets` isn't in AUTO_PICK_SINGLE_OPTION_STEPS:
      // unlike a circumstantial 1-option hostile/friendly roster (#345, real
      // ambiguity risk), self_only audience is structurally ALWAYS exactly
      // this one row, so there's nothing to stray-submit into.
      meta: { healthState: selfChar.healthState, isAlly: true, audienceSelfOnly: true },
    },
  ];
}

export function buildSceneCharOptions(
  partyMembers: GameCharacterInfo[],
  sceneChars: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  // Dedup by charId — party NPCs may appear in both arrays
  const seen = new Set<string>();
  const chars: GameCharacterInfo[] = [];
  for (const c of [...partyMembers.filter((c) => c.charId !== playerCharId), ...sceneChars]) {
    if (!seen.has(c.charId)) {
      seen.add(c.charId);
      chars.push(c);
    }
  }
  if (chars.length === 0) return [];
  return chars.map((c) => ({
    name: c.name,
    description: c.isPartyMember ? 'Party member' : (c.type ?? 'NPC'),
    isMenuOption: true,
    sourceId: c.charId,
    stageLabel,
    emoji: c.avatarEmoji,
    avatarUrl: c.avatarUrl ?? c.portraitUrl,
  }));
}

export function buildSceneNpcOptions(
  sceneChars: GameCharacterInfo[],
  stageLabel?: string
): SlashCommandDefinition[] {
  // #298: a dead character can't be invited to the party — drop corpses from
  // the /invite picker. Mirrors the `healthState !== 'dead'` filter on the
  // other scene pickers (buildEnemyOptions / buildAllyOptions).
  const invitable = sceneChars.filter((c) => c.healthState !== 'dead');
  if (invitable.length === 0) return [];
  return invitable.map((c) => ({
    name: c.name,
    description: c.type ?? 'NPC',
    isMenuOption: true,
    sourceId: c.charId,
    stageLabel,
    emoji: c.avatarEmoji,
    avatarUrl: c.avatarUrl ?? c.portraitUrl,
  }));
}

export function buildPartyMemberOptions(
  partyMembers: GameCharacterInfo[],
  playerCharId: string | null,
  stageLabel?: string
): SlashCommandDefinition[] {
  const members = partyMembers.filter((c) => c.charId !== playerCharId);
  if (members.length === 0) return [];
  return members.map((c) => ({
    name: c.name,
    description: healthLabel(c),
    isMenuOption: true,
    sourceId: c.charId,
    stageLabel,
    emoji: c.avatarEmoji,
    avatarUrl: c.avatarUrl ?? c.portraitUrl,
  }));
}

/** Spell info parsed from character data */
export interface SpellOption {
  name: string;
  emoji?: string;
  summary?: string;
  level: number;
  slotsRemaining?: number;
  maxSlots?: number;
  isCantrip: boolean;
  /** True when the spell is granted by species lineage / origin feat and the
   *  character has no spell-slot pool at this level (e.g. Wood Elf Monk's
   *  Longstrider). The runtime free-cast pipeline gates the actual quota;
   *  when `freeCastRemaining` is 0, the UI should render it as depleted. */
  freeCastable?: boolean;
  /** Remaining innate free-cast uses until next long rest. Only set when
   *  `freeCastable === true`. Computed from `session.feature_uses[spell_name]`
   *  — same HashMap for species grants, Magic Initiate, and any future feat
   *  with a per-spell quota. When 0, the spell is NOT castable without a slot. */
  freeCastRemaining?: number;
  /** Maximum innate free casts per long rest. Only set when `freeCastable === true`.
   *  1 for Elf/Tiefling L3+L5 grants and Magic Initiate feat; Proficiency Bonus for
   *  Forest Gnome Speak with Animals. */
  freeCastMax?: number;
}

/** Context needed to compute innate free-cast quotas.
 *
 *  When this is omitted, innate spells render as clickable-but-quota-unknown
 *  (pre-April-2026 behaviour). When it's provided, `collectSpellOptions`
 *  populates `freeCastRemaining` / `freeCastMax` on each freeCastable spell
 *  so the UI can show a `1/1` badge and disable exhausted buttons. */
export interface FreeCastContext {
  /** `session.feature_uses[spell_name]` — per-spell usage counter for species
   *  grants AND Magic Initiate feat spells (same HashMap, one source of truth). */
  featureUses?: Record<string, number>;
  /** Lowercased names of every Magic Initiate L1 spell granted by the character's
   *  feats (`feats[].grants_spells.level_1_spell`). Magic Initiate is repeatable
   *  with different spell lists (SRD 5.2.1), so a character can carry multiple
   *  feat spells simultaneously — Wizard + MI Cleric (Bless) + MI Druid (Healing
   *  Word) must track all three independently. Case-insensitive Set lookup
   *  routes every feat spell through the MI quota path in `computeFreeCastQuota`. */
  magicInitiateSpells?: Set<string>;
  /** Character proficiency bonus — used for Forest Gnome Speak with Animals quota (PB/LR). */
  proficiencyBonus?: number;
  /** True for Forest Gnome characters — Speak with Animals uses PB/LR quota. */
  isForestGnome?: boolean;
  /** True when the character's class has no class-level spellcasting (Barbarian /
   *  Fighter / Monk / Rogue). Derived authoritatively from `permanent.class` rather
   *  than heuristically from an empty `max_spell_slots` table, which produces a
   *  vacuous-true false positive for a full caster whose slot table is transiently
   *  empty (stale snapshot, pre-enrichment load). Used by `collectSpellOptions` to
   *  decide whether `always_prepared` entries should render as freeCastable innate
   *  spells or fall through to normal slot consumption. */
  isClassNonCaster?: boolean;
}

/** Class names (case-insensitive) that have no class-level spellcasting feature.
 *  Any `always_prepared` spell on these classes must come from species lineage or
 *  a feat (Magic Initiate), routed through the free-cast pipeline. */
const NON_CASTER_CLASSES = new Set(['barbarian', 'fighter', 'monk', 'rogue']);

/** Narrow subset of `CharacterPermanent` that `buildFreeCastContext` actually
 *  reads. Using a `Partial<Pick<…>>` instead of the full type means test
 *  fixtures don't have to fabricate unused required fields (id/name/type/…),
 *  while real callers still pass the full `CharacterPermanent` by structural
 *  subtyping. */
export type FreeCastPermanent = Partial<
  Pick<CharacterPermanent, 'feats' | 'species' | 'lineage' | 'class' | 'proficiency_bonus'>
>;

/** Narrow subset of `CharacterSession` that `buildFreeCastContext` actually
 *  reads. See `FreeCastPermanent` for rationale. */
export type FreeCastSession = Partial<Pick<CharacterSession, 'feature_uses' | 'class_resources'>>;

/** Build a FreeCastContext from a character's `permanent` + `session` data.
 *
 *  Shared between CharDetailsPanel (Actions panel) and ChatPanel (slash menu
 *  + Ctrl+N shortcuts) so both surfaces evaluate the same quota. Both inputs
 *  are optional — missing data yields an empty-ish context that still drives
 *  the Magic Initiate / species lookups when partial data is available.
 *
 *  Types come from `@danda/shared` (round-tripped from the Rust session state),
 *  so `feature_uses` / `proficiency_bonus` / `class` are already well-typed —
 *  the only narrow cast is for `feat.grants_spells`, which the base feats type
 *  doesn't model (added dynamically by the enricher). */
export function buildFreeCastContext(
  permanent: FreeCastPermanent | null | undefined,
  session: FreeCastSession | null | undefined
): FreeCastContext {
  const feats = permanent?.feats ?? [];
  // Collect EVERY MI feat's L1 spell — Magic Initiate is repeatable per SRD 5.2.1
  // with distinct spell lists, so a character can hold multiple feat spells at
  // once. Backend tracks each independently via `session.feature_uses[spell_name]`;
  // the Set here lets `computeFreeCastQuota` route every feat spell through the
  // MI quota branch. `.trim()` + `.toLowerCase()` normalize LLM output ("Bless "
  // vs "bless") so downstream Set lookups match regardless of casing drift.
  const magicInitiateSpells = new Set(
    feats
      .map(
        (f) => (f as { grants_spells?: { level_1_spell?: string } }).grants_spells?.level_1_spell
      )
      .filter((name): name is string => !!name && name.trim().length > 0)
      .map((name) => name.trim().toLowerCase())
  );

  const speciesLower = (permanent?.species ?? '').trim().toLowerCase();
  const lineageLower = (permanent?.lineage ?? '').trim().toLowerCase();
  const isForestGnome = speciesLower.includes('gnome') && lineageLower.includes('forest');

  // Leave `isClassNonCaster` undefined when the class isn't known — the
  // collectSpellOptions fallback heuristic (empty max_spell_slots) kicks in for
  // legacy/partial data. Only set a definitive true/false when the class name
  // is available so the empty-slot-table false positive can't override a real
  // caster. `.trim()` guards against LLM output like `"Fighter "` with a
  // trailing space — without it, the Set lookup would miss and
  // `isClassNonCaster` would flip to `false`, silently stripping the
  // feat-granted Bless freeCastable flag.
  const classLower = (permanent?.class ?? '').trim().toLowerCase();
  const isClassNonCaster: boolean | undefined =
    classLower === '' ? undefined : NON_CASTER_CLASSES.has(classLower);

  return {
    featureUses: session?.feature_uses,
    magicInitiateSpells,
    proficiencyBonus: permanent?.proficiency_bonus,
    isForestGnome,
    isClassNonCaster,
  };
}

/** Format the slot-count suffix for a spell description.
 *
 *  Cantrips and freeCastable spells (species lineage / Magic Initiate) have no
 *  slot pool — rendering " — undefined/undefined slots" for those is a visible
 *  UI bug that ships when slotsRemaining/maxSlots are undefined. Return an
 *  empty string for those cases; the caller concatenates unconditionally.
 */
export function formatSlotInfo(spell: SpellOption): string {
  if (spell.isCantrip || spell.freeCastable) return '';
  return ` — ${spell.slotsRemaining}/${spell.maxSlots} slots`;
}

type SpellMechanicsSummary = Array<{
  name: string;
  emoji?: string;
  summary?: string;
  base_level: number;
  /** Full SpellEffect (optional — present on real SpellData). The picker reads
   *  this to detect multi-target buffs (Bless: `effect.type === 'buff'` with
   *  `max_targets > 1`) and switch the target step to multi-select. */
  effect?: SpellEffect;
  /** SRD casting time. Reaction-cast spells (Shield, Counterspell, Hellish
   *  Rebuke, Feather Fall) carry `'reaction'`; the menu
   *  filter uses this to hide them from the proactive `/cast` flow without
   *  maintaining a hand-rolled name list. */
  casting_time?: string;
  /** Which characters the FE target-picker offers for this spell. Backend
   *  derives it from effect + range (`SpellData::refresh_target_audience`)
   *  and ships it on the wire; absent on legacy fixtures ⇒ treat as `'any'`
   *  (full roster, no regression). Menu-only — typed/free-text casts still
   *  resolve any target. */
  target_audience?: SpellTargetAudience;
}>;
/** A prepared spell with its base level. Level 0 = cantrip, 1-9 = leveled spell (D&D convention). */
export type PreparedSpellEntry = { name: string; level: number };

function toSpellName(entry: string | { name: string }): string {
  if (typeof entry === 'string') return entry;
  return entry.name;
}

/** Spellbook level row used by the lobby + game-session "Spellbook" pane.
 *  Mirrors `SpellbookLevel` in `CharDetailsSections` — kept structural here
 *  to avoid a circular import. */
export interface MergedSpellbookLevel {
  level: number;
  spells: Array<{ name: string; emoji?: string; summary?: string }>;
}

/** Merge `spellcasting.spells_known_by_level` and `spellcasting.always_prepared`
 *  into a single spellbook view, deduped case-insensitively by spell name and
 *  sorted by level. `always_prepared` is the source of truth for feat-granted
 *  spells (Magic Initiate Cure Wounds) and subclass/domain spells (Cleric
 *  domain, Paladin oath); folding it into the spellbook ensures those spells
 *  surface alongside the class's known list.
 *
 *  Resolves the level for each `always_prepared` entry from its `base_level`
 *  when present, falling back to a `spell_mechanics` lookup; entries with no
 *  resolvable level are dropped (defensive — backend always sends one).
 *
 *  Hunter's Mark appears in both lists for Rangers — dedupe is mandatory to
 *  avoid double-rendering. */
export function mergeSpellbookLevels(
  spellcasting: GameSpellcastingInfo | undefined,
  spellMechanics?: SpellMechanicsSummary
): MergedSpellbookLevel[] {
  if (!spellcasting) return [];

  const emojiByName = new Map(
    (spellMechanics ?? [])
      .filter((s) => s.emoji && s.name)
      .map((s) => [s.name.toLowerCase(), s.emoji!])
  );
  const summaryByName = new Map(
    (spellMechanics ?? [])
      .filter((s) => s.summary && s.name)
      .map((s) => [s.name.toLowerCase(), s.summary!])
  );
  const levelByName = new Map(
    (spellMechanics ?? []).map((s) => [s.name.toLowerCase(), s.base_level])
  );

  const byLevel = new Map<number, Array<{ name: string; emoji?: string; summary?: string }>>();
  const seen = new Set<string>();

  const pushSpell = (rawName: string, level: number) => {
    const name = rawName.trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const bucket = byLevel.get(level) ?? [];
    bucket.push({
      name,
      emoji: emojiByName.get(key),
      summary: summaryByName.get(key),
    });
    byLevel.set(level, bucket);
  };

  // Source 1: spells_known_by_level
  for (const [levelStr, spells] of Object.entries(spellcasting.spells_known_by_level ?? {})) {
    if (!spells?.length) continue;
    const level = Number(levelStr);
    for (const entry of spells) {
      pushSpell(toSpellName(entry), level);
    }
  }

  // Source 2: always_prepared (domain / subclass / feat grants). Cure Wounds
  // from Magic Initiate (Cleric) only lives here.
  for (const entry of spellcasting.always_prepared ?? []) {
    const name = toSpellName(entry);
    const level =
      typeof entry === 'object' && 'base_level' in entry
        ? (entry as { base_level?: number }).base_level
        : levelByName.get(name.toLowerCase());
    if (level == null) continue;
    pushSpell(name, level);
  }

  return Array.from(byLevel.entries())
    .sort(([a], [b]) => a - b)
    .map(([level, spells]) => ({ level, spells }));
}

/** Compute the remaining free-cast quota for an innate spell.
 *
 *  Both Magic Initiate and species paths read `featureUses[spell_name]` —
 *  single source of truth for per-spell tracking, mirroring the Rust
 *  backend's `session.feature_uses` HashMap which `cast_spell.rs` increments
 *  on every MI / species free cast via `consume_feature_use`. Long rest
 *  clears the HashMap server-side, restoring all quotas.
 *
 *  When `context` is undefined, returns `{ remaining: undefined, max: undefined }`
 *  and the UI falls back to "always clickable" behaviour. */
function computeFreeCastQuota(
  spellName: string,
  context: FreeCastContext | undefined
): { remaining?: number; max?: number } {
  if (!context) return {};

  // Per-spell usage lookup. Case-insensitive to tolerate LLM casing drift,
  // matching the Rust lookup at intent/mod.rs.
  const nameLower = spellName.toLowerCase();
  const usedEntry = Object.entries(context.featureUses ?? {}).find(
    ([key]) => key.toLowerCase() === nameLower
  );
  const featureUsedCount = usedEntry ? usedEntry[1] : 0;

  // Magic Initiate feat path — Set lookup covers every feat's L1 spell, so
  // repeatable MI (e.g. MI Cleric + MI Wizard) correctly renders independent
  // 1/LR quotas per spell. Backend tracks each via `session.feature_uses`
  // keyed by spell name; the Set is populated lowercased for case-insensitive
  // lookup matching the Rust path at `enricher/intent/mod.rs`.
  if (context.magicInitiateSpells?.has(nameLower)) {
    return { remaining: featureUsedCount > 0 ? 0 : 1, max: 1 };
  }

  // Species lineage path: Forest Gnome Speak with Animals = PB/LR,
  // everything else = 1/LR.
  const max =
    context.isForestGnome && nameLower === 'speak with animals' && context.proficiencyBonus
      ? context.proficiencyBonus
      : 1;

  return { remaining: Math.max(0, max - featureUsedCount), max };
}

function collectSpellOptions(
  spellcasting: GameSpellcastingInfo | undefined,
  spellSlotsUsed: Record<number, number> | undefined,
  spellMechanics?: SpellMechanicsSummary,
  preparedSpells?: PreparedSpellEntry[],
  freeCastContext?: FreeCastContext
): SpellOption[] {
  if (!spellcasting) {
    return [];
  }

  // Reaction-only spells (Shield, Counterspell, Hellish Rebuke,
  // Feather Fall) auto-fire via the engine's reaction
  // subscription system on their natural trigger — there's no proactive
  // cast path, so every downstream surface (slash autocomplete via
  // `getSpellCommands`, Ctrl+N + Actions panel via `getNumberedSpells`,
  // the `/cast` menu via `buildSpellOptions`) must hide them.
  //
  // Primary check: `spell_mechanics[name].casting_time === 'reaction'`
  // (sourced from Rust `SpellData.casting_time` via the wire). Adding a
  // new reaction spell to ES data + reindexing surfaces it here with zero
  // FE code change. The hardcoded `REACTION_SPELLS` set in `build-intent.ts`
  // remains as a legacy belt-and-suspenders backstop for the rare case
  // where mechanics haven't loaded yet.
  const castingTimeByName = new Map(
    (spellMechanics ?? [])
      .filter((s) => s.casting_time)
      .map((s) => [s.name.toLowerCase(), s.casting_time])
  );
  const isReactionSpell = (name: string) => {
    const key = name.trim().toLowerCase();
    return castingTimeByName.get(key) === 'reaction' || REACTION_SPELLS.has(key);
  };

  const spells: SpellOption[] = [];
  const knownByLevel = spellcasting.spells_known_by_level ?? {};
  const maxSlots = spellcasting.max_spell_slots ?? {};
  const used = spellSlotsUsed ?? {};
  const seen = new Set<string>();
  const spellLevels = new Map(
    (spellMechanics ?? []).map((spell) => [spell.name.toLowerCase(), spell.base_level])
  );
  const spellEmojis = new Map(
    (spellMechanics ?? [])
      .filter((spell) => spell.emoji)
      .map((spell) => [spell.name.toLowerCase(), spell.emoji!])
  );
  const spellSummaries = new Map(
    (spellMechanics ?? [])
      .filter((spell) => spell.summary)
      .map((spell) => [spell.name.toLowerCase(), spell.summary!])
  );
  // When preparedSpells is provided, only show leveled spells from spells_known that are prepared
  const preparedSet = preparedSpells?.length
    ? new Set(preparedSpells.map((s) => s.name.toLowerCase()))
    : null;

  // D&D spell levels: 0 = cantrip (no slot cost, always castable), 1-9 = leveled (uses slots).
  const pushSpell = (name: string, level: number) => {
    const key = name.toLowerCase();
    if (!name || seen.has(key)) {
      return;
    }
    if (isReactionSpell(name)) {
      return;
    }
    seen.add(key);
    const emoji = spellEmojis.get(key);
    const summary = spellSummaries.get(key);

    // Level 0 = cantrip per D&D/SRD convention — always castable, no slot cost
    if (level === 0) {
      spells.push({ name, emoji, summary, level: 0, isCantrip: true });
      return;
    }

    const max = (maxSlots as Record<string, number>)[String(level)] ?? 0;
    const usedCount = used[level] ?? 0;
    spells.push({
      name,
      emoji,
      summary,
      level,
      slotsRemaining: Math.max(0, max - usedCount),
      maxSlots: max,
      isCantrip: false,
    });
  };

  // Source 1: spells_known_by_level (wizard spellbook, known casters)
  for (const [levelStr, spellNames] of Object.entries(knownByLevel)) {
    const level = Number(levelStr);
    if (!spellNames?.length) continue;

    for (const entry of spellNames) {
      const name = toSpellName(entry);
      // Cantrips always shown; leveled spells filtered by prepared list when available
      if (level > 0 && preparedSet && !preparedSet.has(name.toLowerCase())) continue;
      pushSpell(name, level);
    }
  }

  // Source 2: always_prepared spells (domain/subclass/species/feat) — bypass prepared filter.
  // Extract level from entry object when available, fall back to spellMechanics lookup.
  //
  // True non-caster shape: no slot pool at any level. Species/feat innate magic on
  // a Wood Elf Monk / Tiefling Fighter / etc. lands here. Prefer the authoritative
  // class flag when the caller supplied one via `buildFreeCastContext` — a plain
  // `Object.values(maxSlots).every(v => !v)` check returns true vacuously for an
  // empty `{}` table and would mis-label a L1 Cleric whose slots haven't hydrated
  // as a non-caster. Legacy callers without the flag fall back to the slot-table
  // heuristic to preserve backwards compatibility.
  const maxSlotsRecord = maxSlots as Record<string, number>;
  const isNonCaster =
    freeCastContext?.isClassNonCaster === true ||
    (freeCastContext?.isClassNonCaster !== false && Object.values(maxSlotsRecord).every((v) => !v));
  for (const entry of spellcasting.always_prepared ?? []) {
    const name = toSpellName(entry);
    const level =
      typeof entry === 'object' && 'base_level' in entry
        ? (entry as { base_level: number }).base_level
        : spellLevels.get(name.toLowerCase());
    if (level == null) continue;

    // Non-caster with species/feat innate magic: no spell-slot pool anywhere.
    // The runtime free-cast pipeline (FreeCastReason::SpeciesGranted /
    // MagicInitiate) handles the quota. We compute the remaining uses here so
    // the UI can render a `1/1` / `0/1` badge and disable exhausted buttons —
    // long rest clears both `class_resources` and `feature_uses` server-side
    // (see `rest/long_rest.rs:103-104`), so the next frame after a rest shows
    // fresh quotas without any frontend state reset.
    //
    // Known UX gap (pre-existing, not blocker): full casters (Wizard/Cleric/
    // Druid/Sorcerer/Warlock/Bard) with cross-list Magic Initiate take the
    // `pushSpell` branch below because `isNonCaster === false`. Their MI feat
    // spell renders as a normal slot entry and the free-cast badge never
    // appears, even though the backend correctly awards the 1/LR free cast
    // via `detect_free_cast` at runtime (first cast doesn't consume a slot;
    // subsequent casts do). Polymyth-reviewed as "mechanics correct, UX quirk
    // pre-existing". Fix is a new SpellOption mode that carries both slot
    // counts AND free-cast quota simultaneously — deferred pending a proper
    // rendering design.
    // Reaction-only spells are auto-fired by the engine's reaction
    // subscription system on their natural trigger — never show them
    // proactively. Tiefling Infernal grants Hellish Rebuke as a
    // racial 1/LR free cast via `always_prepared`, and the
    // non-caster branch below pushes directly to `spells[]` instead
    // of routing through `pushSpell` (where the reaction filter
    // lives). Guard at the loop head so both the caster and
    // non-caster paths inherit the filter.
    if (isReactionSpell(name)) continue;

    if (level > 0 && isNonCaster) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const { remaining, max } = computeFreeCastQuota(name, freeCastContext);
      spells.push({
        name,
        emoji: spellEmojis.get(key),
        summary: spellSummaries.get(key),
        level,
        isCantrip: false,
        freeCastable: true,
        freeCastRemaining: remaining,
        freeCastMax: max,
      });
      continue;
    }

    pushSpell(name, level);
  }

  // Source 3: session.prepared_spells (daily preparation for Cleric/Druid/Paladin).
  // These carry their own level (0 = cantrip, 1-9 = leveled), so they don't need
  // spells_known_by_level. Dedup via `seen` prevents double-counting spells that
  // also appear in Source 1 or 2.
  for (const spell of preparedSpells ?? []) {
    pushSpell(spell.name, spell.level);
  }

  spells.sort((a, b) => {
    if (a.isCantrip !== b.isCantrip) return a.isCantrip ? -1 : 1;
    if (a.level !== b.level) return a.level - b.level;
    return a.name.localeCompare(b.name);
  });

  return spells;
}

export function buildSpellOptions(
  spellcasting: GameSpellcastingInfo | undefined,
  spellSlotsUsed: Record<number, number> | undefined,
  stageLabel?: string,
  spellMechanics?: SpellMechanicsSummary,
  preparedSpells?: PreparedSpellEntry[],
  freeCastContext?: FreeCastContext,
  /** Current concentration spell name (from `selfChar.session.concentration`),
   *  if any. When set, leveled concentration spells get tagged with
   *  `meta.concentrationToReplace` so the `concentration_confirm` wizard
   *  step renders. Players never see this tag — only the swap-confirm
   *  picker reads it. */
  playerConcentration?: string | null
): SlashCommandDefinition[] {
  const spells = collectSpellOptions(
    spellcasting,
    spellSlotsUsed,
    spellMechanics,
    preparedSpells,
    freeCastContext
  );

  if (spells.length === 0) return [];

  // Filter out non-cantrip spells with 0 remaining slots (can't be cast).
  // `freeCastable` spells are kept in the menu as long as their innate quota
  // is non-zero — when `freeCastRemaining === 0` (long rest exhausted), drop
  // them so `/cast` only shows spells the player can actually cast this rest.
  // Legacy call sites that don't pass a `freeCastContext` keep the pre-April
  // behaviour (all freeCastable spells shown) — the runtime rejects the cast
  // with a warning if the quota is exhausted in that case. Reaction-only
  // spells are already excluded upstream by `collectSpellOptions`.
  const castable = spells.filter((s) => {
    if (s.isCantrip) return true;
    if (s.freeCastable) {
      return s.freeCastRemaining == null || s.freeCastRemaining > 0;
    }
    return (s.slotsRemaining ?? 0) > 0;
  });

  if (castable.length === 0) return [];

  // Build a quick lookup for which spells require concentration. The full
  // SpellData carries `concentration: boolean` but it's not on the local
  // SpellMechanicsSummary subset — type-narrow via cast.
  const concentrationSpells = new Set(
    (spellMechanics ?? [])
      .filter((s) => (s as { concentration?: boolean }).concentration === true)
      .map((s) => s.name.toLowerCase())
  );
  const currentConcLower = playerConcentration?.trim().toLowerCase() ?? null;

  return castable.map((s, i) => {
    const levelLabel = s.isCantrip ? 'Cantrip' : `Level ${s.level}`;
    const slotInfo = formatSlotInfo(s);
    // Ctrl+1 through Ctrl+0 shortcuts for first 10 spells
    const shortcutHint = i < 10 ? `Ctrl+${(i + 1) % 10}` : undefined;
    // Tag concentration spells with the name of the conc they'd replace,
    // so the wizard's `concentration_confirm` step knows to render.
    // Re-casting the SAME spell isn't a swap (cantrip refresh / re-target
    // is allowed) — skip the confirm in that case.
    const nameLower = s.name.toLowerCase();
    const wouldReplace =
      currentConcLower && currentConcLower !== nameLower && concentrationSpells.has(nameLower)
        ? playerConcentration!
        : undefined;
    return {
      name: s.name,
      emoji: s.emoji,
      description: `${levelLabel}${slotInfo}`,
      isMenuOption: true,
      sourceId: s.name,
      stageLabel,
      meta: {
        spellLevel: s.level,
        slotsRemaining: s.slotsRemaining,
        // Propagate freeCast so `slot_level_choice` can offer an Innate
        // (slotless) row for non-caster MI/species-grant leveled spells.
        // Without this, `useGameSlashCommands.slot_level_choice` reads
        // `meta.freeCast` as undefined, treats the cast as slot-paid,
        // finds an empty `max_spell_slots` table on a non-caster, and
        // resets the wizard with "No spell slot to spend." The per-spell
        // shortcut path (`getSpellCommands` below at ~L1813) already sets
        // this on command meta; this mirrors it for the generic /cast row.
        freeCast: s.freeCastable === true,
        ...(wouldReplace ? { concentrationToReplace: wouldReplace } : {}),
      },
      shortcutHint,
    };
  });
}

/** Equippable item kinds (weapon/armor/wondrous_item) live in `@danda/shared`
 *  alongside `GameEquipmentItem.item_kind`. */
const EQUIPPABLE_ITEM_KINDS_SET = new Set<string>(EQUIPPABLE_ITEM_KINDS);

function formatItemDescription(item: { item_kind?: string }): string {
  return item.item_kind?.replace(/_/g, ' ') ?? '';
}

/** Group identical inventory entries (same display name + kind) into a single
 *  picker row with a `×N` quantity suffix in the description. The pick fires
 *  against the first instance's `item_id` — actions that consume one item at
 *  a time (give/drop/equip/use) work correctly; multi-instance actions
 *  (already opt out by re-opening the menu) are unaffected.
 *
 *  Without this, 5 unstacked Daggers render as 5 separate rows in the
 *  picker (verified Run 2 / 2026-05-31 — `/give` showed Dagger ×5 as
 *  five entries). The Inventory dialog already groups via `×N`; the
 *  slash picker now matches.
 */
function dedupeStackedItems<T extends { name: string; item_kind?: string; item_id?: string }>(
  items: T[]
): Array<{ representative: T; count: number }> {
  const groups = new Map<string, { representative: T; count: number }>();
  for (const item of items) {
    const key = `${item.name}|${item.item_kind ?? ''}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      groups.set(key, { representative: item, count: 1 });
    }
  }
  return [...groups.values()];
}

/** Append `· ×N` to a description when `count > 1`. */
function withQuantitySuffix(desc: string, count: number): string {
  if (count <= 1) return desc;
  const suffix = `×${count}`;
  return desc ? `${desc} · ${suffix}` : suffix;
}

/** Names of items that are quest-critical across the current modules. Used as
 *  a fallback when `Item.is_quest_item` hasn't been hydrated on existing
 *  in-flight inventory items (the flag lives on the Item struct + is tagged
 *  in module.json, but existing characters mid-game won't have it set until a
 *  re-acquire / migration). Adding a name here is a temporary safety net so
 *  the /give filter behaves correctly today; the long-term fix is backend
 *  hydration at session load (lookup by name in active module's
 *  magic_items list, set is_quest_item=true). Keep this list short —
 *  module-specific knowledge in the FE is technical debt. */
const KNOWN_QUEST_ITEM_NAMES = new Set<string>([
  'Amulet of Theron', // Goldvale's Last Hope — Dragon Ward artifact
]);

export function buildInventoryItemOptions(
  equipment: GameCharacterInfo['equipment'],
  stageLabel?: string,
  opts?: { excludeQuestItems?: boolean; excludeWondrous?: boolean }
): SlashCommandDefinition[] {
  if (!equipment?.length) return [];
  // Filter quest items only when the caller asks (e.g. /give). /equip
  // continues to see them so the player can wear the artifact. The flag
  // rides on `Item.is_quest_item` (Rust) → `GameEquipmentItem.is_quest_item`
  // (TS wire); absent / false means a normal item. The name-based fallback
  // covers existing in-flight items whose flag hasn't been hydrated yet.
  // /drop additionally filters wondrous items (rings, amulets, robes, …)
  // because they're either quest artifacts or attuned magic — never the
  // thing a player means to fling on the ground.
  let source = equipment;
  if (opts?.excludeQuestItems) {
    source = source.filter((item) => !item.is_quest_item && !KNOWN_QUEST_ITEM_NAMES.has(item.name));
  }
  if (opts?.excludeWondrous) {
    source = source.filter((item) => item.item_kind !== 'wondrous_item');
  }
  return dedupeStackedItems(source).map(({ representative: item, count }) => {
    return {
      name: item.name,
      description: withQuantitySuffix(formatItemDescription(item), count),
      isMenuOption: true,
      sourceId: item.item_id ?? item.name,
      stageLabel,
      meta: { itemName: item.name, itemId: item.item_id },
    };
  });
}

export function buildUsableItemOptions(
  equipment: GameCharacterInfo['equipment'],
  stageLabel?: string
): SlashCommandDefinition[] {
  const consumableKinds = new Set(['consumable', 'potion', 'scroll', 'tool']);
  const usable = (equipment ?? []).filter((item) => consumableKinds.has(item.item_kind ?? ''));
  if (usable.length === 0) return [];
  return dedupeStackedItems(usable).map(({ representative: item, count }) => {
    return {
      name: item.name,
      description: withQuantitySuffix(formatItemDescription(item), count),
      isMenuOption: true,
      sourceId: item.item_id ?? item.name,
      stageLabel,
      // `itemKind` drives the conditional second step in `useGameSlashCommands`:
      // potions chain into `friendly_targets`; scrolls/tools/etc. finalize
      // without a target picker (different items expect different target shapes
      // — Scroll of Fireball wants enemies, Thieves' Tools wants scene objects).
      meta: { itemName: item.name, itemId: item.item_id, itemKind: item.item_kind },
    };
  });
}

export function buildEquippableItemOptions(
  equipment: GameCharacterInfo['equipment'],
  stageLabel?: string
): SlashCommandDefinition[] {
  const equippable = (equipment ?? []).filter((item) =>
    EQUIPPABLE_ITEM_KINDS_SET.has(item.item_kind ?? '')
  );
  if (equippable.length === 0) return [];
  return dedupeStackedItems(equippable).map(({ representative: item, count }) => {
    const desc = [formatItemDescription(item)];
    if (item.damage) desc.push(item.damage);
    if (item.ac_bonus) desc.push(`+${item.ac_bonus} AC`);
    return {
      name: item.name,
      description: withQuantitySuffix(desc.filter(Boolean).join(' · '), count),
      isMenuOption: true,
      sourceId: item.item_id ?? item.name,
      stageLabel,
      meta: { itemName: item.name, itemId: item.item_id },
    };
  });
}

export function buildSceneItemOptions(
  sceneItems: SceneItem[],
  stageLabel?: string
): SlashCommandDefinition[] {
  // Looted items are physically removed; filter non-lootable fixtures (wall sconces, etc.)
  const available = sceneItems.filter((i) => i.lootable);
  if (available.length === 0) return [];
  return [
    {
      name: 'All',
      description: 'Pick up everything lootable',
      isMenuOption: true,
      sourceId: PICKUP_ALL_SENTINEL,
      stageLabel,
      meta: { itemName: PICKUP_ALL_SENTINEL },
    },
    ...available.map((item) => ({
      name: item.name,
      description: item.location ?? '',
      isMenuOption: true,
      sourceId: item.id,
      stageLabel,
      meta: { itemName: item.name },
    })),
  ];
}

export function buildSceneObjectOptions(
  interactables: SceneInteractable[],
  stageLabel?: string
): SlashCommandDefinition[] {
  if (interactables.length === 0) return [];
  return interactables.map((obj) => {
    // Only openable/triggerable types have a meaningful state to surface in
    // the picker description. Corpses/statues/furniture (`other`) show just
    // the type so we don't display `other · closed` on a frozen corpse
    // (issues #181 / #235 / #249).
    const showState = ['door', 'chest', 'lever', 'switch', 'trap'].includes(obj.type);
    return {
      name: obj.name,
      description: showState ? `${obj.type} · ${obj.state}` : obj.type,
      isMenuOption: true,
      sourceId: obj.id,
      stageLabel,
    };
  });
}

export function buildOpenableObjectOptions(
  interactables: SceneInteractable[],
  stageLabel?: string
): SlashCommandDefinition[] {
  const openable = interactables.filter((obj) => obj.state === 'closed' || obj.state === 'locked');
  if (openable.length === 0) return [];
  return openable.map((obj) => ({
    name: obj.name,
    description: obj.state === 'locked' ? 'Locked' : 'Closed',
    isMenuOption: true,
    sourceId: obj.id,
    stageLabel,
  }));
}

export function buildClosableObjectOptions(
  interactables: SceneInteractable[],
  stageLabel?: string
): SlashCommandDefinition[] {
  // Gate on type AND state. Without the type filter, every non-openable
  // object (corpses, statues, murals, seals — all default to `Open` since
  // ca3caa53a) would surface in the /close menu, where selecting one fires
  // a close intent on something that has no semantic closed state. Types
  // come from the shared `CLOSABLE_INTERACTABLE_TYPES` constant so the
  // state-badge gate in `SceneItemsSection.tsx` and this picker can't drift.
  const closable = interactables.filter(
    (obj) =>
      obj.state === 'open' && (CLOSABLE_INTERACTABLE_TYPES as readonly string[]).includes(obj.type)
  );
  if (closable.length === 0) return [];
  return closable.map((obj) => ({
    name: obj.name,
    description: 'Open',
    isMenuOption: true,
    sourceId: obj.id,
    stageLabel,
  }));
}

/** Filter to scene interactables that have a `breakable_config`. The /break
 *  picker only lists objects the engine actually knows how to resolve as a
 *  break action — anything without `breakable_config` would no-op. */
export function buildBreakableObjectOptions(
  interactables: SceneInteractable[],
  stageLabel?: string
): SlashCommandDefinition[] {
  const breakable = interactables.filter(
    (obj) => obj.breakable_config != null && obj.state !== 'destroyed'
  );
  if (breakable.length === 0) return [];
  return breakable.map((obj) => {
    const cfg = obj.breakable_config!;
    const hp = typeof obj.current_hp === 'number' ? obj.current_hp : cfg.hp;
    return {
      name: obj.name,
      description: `AC ${cfg.ac} · ${hp}/${cfg.hp} HP`,
      isMenuOption: true,
      sourceId: obj.id,
      stageLabel,
    };
  });
}

// =============================================================================
// Item Spell & Ability Option Builders
// =============================================================================

export function buildItemSpellOptions(
  spells: Array<{ spellName: string; charges: number }>,
  itemCharges: number,
  stageLabel?: string
): SlashCommandDefinition[] {
  const affordable = spells.filter((s) => s.charges <= itemCharges);
  if (affordable.length === 0) return [];
  return affordable.map((s) => ({
    name: s.spellName,
    description: `${s.charges} charge${s.charges !== 1 ? 's' : ''}`,
    isMenuOption: true,
    sourceId: s.spellName,
    stageLabel,
    meta: { spellName: s.spellName, charges: s.charges },
  }));
}

export function buildItemAbilityOptions(
  abilities: Array<{ name: string; trigger: string; charges?: number }>,
  stageLabel?: string
): SlashCommandDefinition[] {
  const onUse = abilities.filter((a) => a.trigger === 'on_use');
  if (onUse.length === 0) return [];
  return onUse.map((a) => ({
    name: a.name,
    description: a.charges != null ? `${a.charges} charge${a.charges !== 1 ? 's' : ''}` : '',
    isMenuOption: true,
    sourceId: a.name,
    stageLabel,
    meta: { abilityName: a.name },
  }));
}

// =============================================================================
// Species Ability Target Type Derivation
// =============================================================================

/** Effect types (snake_case from Rust serde) where the ability targets self */
const SELF_ONLY_EFFECTS = new Set([
  'self_healing',
  'extra_action',
  'transform',
  'self_cleanse',
  'resource_conversion',
  'resource_restoration',
  'toggle_buff',
  'resistance_selection',
  'area_protection',
  // extra_damage intentionally excluded — on-hit modifiers (Sneak Attack, Fire's Burn)
  // don't need standalone buttons; the target is determined by the attack itself
  'reroll',
  'summon_companion',
]);

/** Effect types where the ability needs a target */
const TARGETED_EFFECTS = new Set([
  'area_damage',
  'save_or_suffer',
  'target_healing',
  'pool_healing',
  'touch_pool_healing',
  'area_split_effect',
]);

/**
 * Derive whether a species/class ability targets self or needs a target picker.
 * Returns null for passives/reactions/unknowns that shouldn't appear as action buttons.
 */
export function deriveAbilityTargetType(
  ability: Record<string, unknown>
): 'self_only' | 'targeted' | null {
  const effect = ability.effect as Record<string, unknown> | undefined;
  if (!effect?.type || effect.type === 'none') return null;

  const effectType = effect.type as string;

  // Buff: self vs targeted depends on the target field
  // Rust TargetType::Self_ serializes as "self" via #[serde(rename = "self")]
  if (effectType === 'buff') {
    const target = effect.target as string | undefined;
    return target === 'self' ? 'self_only' : 'targeted';
  }

  if (SELF_ONLY_EFFECTS.has(effectType)) return 'self_only';
  if (TARGETED_EFFECTS.has(effectType)) return 'targeted';

  // Special: check description for action keywords
  if (effectType === 'special') {
    const desc = ((ability.description as string) ?? '').toLowerCase();
    // Skip reactions — they're handled by the reaction system, not use_ability
    if (desc.includes('passive reaction') || desc.startsWith('reaction:')) return null;
    // Skip auto-applied on-hit abilities — handled by the attack pipeline, not use_ability
    if (desc.includes('when you hit')) return null;
    if (
      desc.includes('teleport') ||
      desc.includes('dash') ||
      desc.includes('speed') ||
      desc.includes('tremorsense') ||
      desc.includes('bonus action')
    ) {
      return 'self_only';
    }
    return null; // skip unknown specials
  }

  // extra_damage: ALL are auto-applied in the attack pipeline (class AND species on-hit).
  // No standalone buttons needed — they fire automatically when you hit.
  if (effectType === 'extra_damage') {
    return null;
  }

  return null;
}

// =============================================================================
// Dynamic Command Generation
// =============================================================================

/** Map a spell's `target_audience` to the completion step that builds the
 *  curated target roster.
 *
 *  - `'hostile'` → `'enemies'` (Fire Bolt, Fireball — only foes)
 *  - `'friendly'` → `'friendly_targets'` (Bless, Cure Wounds — self + allies +
 *    non-hostile NPCs)
 *  - `'self_only'` → `'all_targets'` (Mage Armor, Shield — the runtime
 *    substitution in the wizard collapses this to the lone Self row)
 *  - `'any'` / undefined → `'all_targets'` (Dispel, Polymorph, or legacy
 *    fixtures with no audience — full roster, no regression)
 *
 *  `self_only` and `any` both map to `all_targets` here; the per-picked-spell
 *  runtime substitution in `useGameSlashCommands` distinguishes them (self_only
 *  → single Self row, any → everyone). For statically-built per-spell commands
 *  (`getSpellCommands`) the audience is known up front, so `self_only` still
 *  resolves to the Self-only roster via that same substitution. */
export function targetStepForAudience(
  audience: SpellTargetAudience | undefined
): CompletionStepType {
  switch (audience) {
    case 'hostile':
      return 'enemies';
    case 'friendly':
      return 'friendly_targets';
    case 'self_only':
    case 'any':
    default:
      return 'all_targets';
  }
}

/** Generate spell-based slash commands from character data */
export function getSpellCommands(
  spellcasting: GameSpellcastingInfo | undefined,
  spellSlotsUsed: Record<number, number> | undefined,
  spellMechanics?: SpellMechanicsSummary,
  preparedSpells?: PreparedSpellEntry[],
  freeCastContext?: FreeCastContext,
  /** Current concentration spell name (from `selfChar.session.concentration`),
   *  used to prepend a `concentration_confirm` step to per-spell commands
   *  whose pick would swap an active concentration. Without this, the
   *  per-spell slash flow bypasses the swap-confirm dialog entirely. */
  playerConcentration?: string | null
): GameSlashCommand[] {
  const spells = collectSpellOptions(
    spellcasting,
    spellSlotsUsed,
    spellMechanics,
    preparedSpells,
    freeCastContext
  ).filter((spell) => {
    if (spell.isCantrip) return true;
    if (spell.freeCastable) {
      return spell.freeCastRemaining == null || spell.freeCastRemaining > 0;
    }
    return (spell.slotsRemaining ?? 0) > 0;
  });
  if (spells.length === 0) return [];

  // Concentration lookup: `spell_mechanics[name].concentration === true`.
  const concentrationSpells = new Set(
    (spellMechanics ?? [])
      .filter((s) => (s as { concentration?: boolean }).concentration === true)
      .map((s) => s.name.toLowerCase())
  );
  const currentConcLower = playerConcentration?.trim().toLowerCase() ?? null;

  // Per-spell target audience — the mechanics are in scope at build time, so
  // each per-spell shortcut can pick its curated target step statically
  // (Fire Bolt → enemies, Cure Wounds → friendly_targets) instead of always
  // opening the full roster. Absent audience ⇒ `all_targets` (no regression).
  const audienceByName = new Map(
    (spellMechanics ?? [])
      .filter((s) => s.target_audience)
      .map((s) => [s.name.toLowerCase(), s.target_audience!])
  );

  const commands: GameSlashCommand[] = [];
  for (const spell of spells) {
    const cmdName = spell.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const levelLabel = spell.isCantrip ? 'Cantrip' : `Level ${spell.level}`;
    const slotInfo = formatSlotInfo(spell);

    // Conditionally prepend `concentration_confirm` so picking a per-
    // spell shortcut that would replace an active concentration still
    // surfaces the swap dialog. The wizard reads the current conc name
    // + picked spell name from `cmd.meta` (set below) since this flow
    // has no prior `spells` selection to carry `concentrationToReplace`.
    const nameLower = spell.name.toLowerCase();
    const wouldReplace =
      currentConcLower && currentConcLower !== nameLower && concentrationSpells.has(nameLower)
        ? playerConcentration!
        : null;

    // Revive spells target a DEAD ally, so they must bypass the audience step
    // (backend derives their audience as `friendly`/`any`, which would route to
    // the dead-excluding friendly/all-target pickers). Mirrors mobile.
    const targetStep: CompletionStepType = REVIVE_SPELLS.has(nameLower)
      ? 'dead_allies'
      : targetStepForAudience(audienceByName.get(nameLower));

    commands.push({
      name: cmdName,
      emoji: spell.emoji,
      description: `Cast ${spell.name} (${levelLabel}${slotInfo})`,
      intentType: 'cast_spell',
      category: 'combat',
      roomTypes: ['game'],
      // Per-spell shortcut threads `slot_level_choice` before the target
      // so the player sees the upcast option. The step auto-picks for
      // cantrips + free-cast spells + leveled spells with only the base
      // slot, so it's invisible UX in those cases. The final target step is
      // curated by the spell's audience (enemies / friendly_targets /
      // all_targets) instead of always offering the full roster.
      completionSteps: wouldReplace
        ? ['concentration_confirm', 'slot_level_choice', targetStep]
        : ['slot_level_choice', targetStep],
      textTemplate: `Cast ${spell.name} at {target}`,
      meta: {
        spellName: spell.name,
        spellLevel: spell.level,
        isCantrip: spell.isCantrip,
        freeCast: spell.freeCastable === true,
        ...(wouldReplace ? { concentrationToReplace: wouldReplace } : {}),
      },
    });
  }

  return commands;
}

/** Get ordered spell list for Ctrl+N shortcuts AND the Actions panel.
 *
 *  Returns ALL spells including exhausted innate (`freeCastRemaining === 0`)
 *  so the Actions panel can render them as disabled. The `/cast` menu uses
 *  `buildSpellOptions` / `getSpellCommands` which filter exhausted entries. */
export function getNumberedSpells(
  spellcasting: GameSpellcastingInfo | undefined,
  spellSlotsUsed: Record<number, number> | undefined,
  spellMechanics?: SpellMechanicsSummary,
  preparedSpells?: PreparedSpellEntry[],
  freeCastContext?: FreeCastContext
): SpellOption[] {
  return collectSpellOptions(
    spellcasting,
    spellSlotsUsed,
    spellMechanics,
    preparedSpells,
    freeCastContext
  );
}

/**
 * Generate ability-based slash commands from `permanent.action_cards`.
 *
 * Delegates to the shared `buildCardSlashCommand` so autocomplete and the
 * Actions panel click path emit identical command shapes — same `intentType`,
 * same `meta.cardIntent`, same `completionSteps`. The previous in-place
 * construction hardcoded `intentType: 'use_ability'` and never attached the
 * structured card payload, so autocomplete sent bare payloads that the
 * backend denylist rejected for Flurry of Blows, multi-mode Cunning
 * Action/Step of the Wind/Patient Defense, and Metamagic.
 */
export function getAbilityCommands(
  actionCards?: ActionCard[],
  cardPoolRemaining?: Record<string, number>,
  /**
   * Card ids whose resource is spent. The Actions panel greys these out; the
   * slash menu used to list them anyway, because the only filter here was on
   * action cost. A depleted Channel Divinity, Second Wind or Arcane Recovery
   * was one keystroke from a backend rejection. Resolved by the caller through
   * `resolveCardUses`, the same resolver the click path uses, so the two
   * cannot drift.
   */
  spentCardIds?: ReadonlySet<string>
): GameSlashCommand[] {
  if (!actionCards?.length) return [];
  const commands: GameSlashCommand[] = [];
  for (const card of actionCards) {
    if (spentCardIds?.has(card.id)) continue;
    // Reaction-cost cards (Shield, Parry, Riposte, Uncanny Dodge, etc.)
    // auto-fire via the reaction subscription system on their natural
    // trigger — there's no proactive activation path, so listing them
    // in the slash menu just lands the player in the engine's rejection
    // softener. Hide them entirely.
    if (card.availability.action_cost === 'reaction') continue;
    // Pool-spend cards (Paladin Lay on Hands) need the current pool remaining
    // wired into `meta.poolRemaining` at build time so the typed-slash path
    // matches the click path. Without this, the picker shows "Pool empty"
    // and the intent fires with pool_spend=None (engine defaults to 1 HP).
    const remaining = cardPoolRemaining?.[card.id];
    const opts = remaining != null ? { poolRemaining: remaining } : undefined;
    const cmd = buildCardSlashCommand(card, opts);
    if (cmd) commands.push(cmd);
  }
  return commands;
}

/** Get all available game commands for current character + mode */
export function getAvailableGameCommands(
  inCombat: boolean,
  spellcasting?: GameSpellcastingInfo,
  spellSlotsUsed?: Record<number, number>,
  spellMechanics?: SpellMechanicsSummary,
  preparedSpells?: PreparedSpellEntry[],
  actionCards?: ActionCard[],
  freeCastContext?: FreeCastContext
): GameSlashCommand[] {
  const mode = inCombat ? 'combat' : 'exploration';
  const staticCmds = GAME_COMMANDS.filter((cmd) => {
    if (!cmd.gameModes) return true;
    return cmd.gameModes.includes(mode);
  });

  const spellCmds = getSpellCommands(
    spellcasting,
    spellSlotsUsed,
    spellMechanics,
    preparedSpells,
    freeCastContext
  );
  const abilityCmds = getAbilityCommands(actionCards);

  return [...staticCmds, ...spellCmds, ...abilityCmds];
}

/**
 * Build HP-amount options for the `pool_spend` completion step (Paladin
 * Lay on Hands, Cleric Preserve Life). Quick-picks scale to pool size so
 * small pools don't show dead options (`10` when pool is 8) and huge pools
 * (Preserve Life at L20 = 100 HP) get useful midpoints.
 *
 *   pool ≤ 5      → every value 1..pool
 *   pool 6–15     → 1, 5, pool
 *   pool 16–50    → 1, 5, 10, pool
 *   pool > 50     → 5, 25, 50, pool
 *
 * Returns `[]` when pool is empty. The trailing "Other amount…" row
 * raises a `request_pool_input` SlashSelectResult action that opens a
 * styled numeric Dialog (rendered by the consumer) for arbitrary
 * values 1..poolMax — skipped when pool is 1 (single value covers).
 */
export function buildPoolSpendOptions(
  poolRemaining: number,
  stageLabel?: string
): SlashCommandDefinition[] {
  if (poolRemaining <= 0) return [];

  let candidates: number[];
  if (poolRemaining <= 5) {
    candidates = Array.from({ length: poolRemaining }, (_, i) => i + 1);
  } else if (poolRemaining <= 15) {
    candidates = [1, 5, poolRemaining];
  } else if (poolRemaining <= 50) {
    candidates = [1, 5, 10, poolRemaining];
  } else {
    candidates = [5, 25, 50, poolRemaining];
  }
  const deduped = [...new Set(candidates.filter((n) => n <= poolRemaining))].sort((a, b) => a - b);

  const rows: SlashCommandDefinition[] = deduped.map((amount) => ({
    name: `${amount} HP`,
    description: amount === poolRemaining ? `All remaining pool (${amount})` : `Spend ${amount} HP`,
    isMenuOption: true,
    sourceId: String(amount),
    stageLabel,
    meta: { poolSpend: amount },
  }));
  if (poolRemaining > 1) {
    rows.push({
      name: 'Other amount…',
      description: `Pick any value 1–${poolRemaining}`,
      isMenuOption: true,
      sourceId: '__pool_custom__',
      stageLabel,
      meta: { isPoolCustomInput: true, poolMax: poolRemaining },
    });
  }
  return rows;
}

/**
 * Build spell-slot-level options for the `spell_slot_levels` step
 * (Bard L5+ Font of Inspiration). Shows each level the caster has at least
 * one remaining slot for, formatted as "Lvl N (M remaining)". Returns `[]`
 * when the caster has no remaining slots — the slash-menu preflight
 * (`commandIsAvailable`) then hides the command entirely instead of
 * surfacing an empty picker.
 */
export function buildSpellSlotLevelOptions(
  maxSpellSlots: Record<string, number> | undefined,
  spellSlotsUsed: Record<number, number> | undefined,
  stageLabel?: string
): SlashCommandDefinition[] {
  if (!maxSpellSlots) return [];
  const used = spellSlotsUsed ?? {};
  const options: { level: number; remaining: number }[] = [];
  for (const [levelStr, max] of Object.entries(maxSpellSlots)) {
    const level = Number(levelStr);
    if (!Number.isFinite(level) || level < 1 || level > 9) continue;
    const remaining = max - (used[level] ?? 0);
    if (remaining > 0) options.push({ level, remaining });
  }
  if (options.length === 0) return [];
  options.sort((a, b) => a.level - b.level);
  return options.map(({ level, remaining }) => ({
    name: `Lvl ${level} (${remaining} remaining)`,
    description: `Expend a level ${level} spell slot`,
    isMenuOption: true,
    sourceId: String(level),
    stageLabel,
    meta: { slotLevel: level },
  }));
}

/**
 * Build options for the `slot_level_choice` step (upcast picker for /cast).
 *
 * - **Cantrip (base 0)**: returns a single "Cantrip" option so auto-pick
 *   immediately advances without prompting.
 * - **Free-cast spell** (Magic Initiate, racial innate at the moment it
 *   has a free use remaining): returns a single "No slot needed" option
 *   carrying `meta.slotLevel = 0` and `meta.freeCast = true`, so the cast
 *   intent ships with `free_cast: true`.
 * - **Leveled spell**: lists each slot level the caster has at least one
 *   remaining slot for, starting at the spell's base level. Base level
 *   renders first so it's the natural default. If only the base level
 *   has slots → 1 option → auto-pick; otherwise the picker opens for
 *   genuine upcast choice.
 *
 * Slot remaining is enforced upstream by the spell-eligibility filter
 * (`getSpellCommands` only surfaces castable spells), so the base level
 * is always present in practice.
 */
export function buildSlotLevelChoiceOptions(
  baseLevel: number,
  isCantrip: boolean,
  isFreeCast: boolean,
  maxSpellSlots: Record<string, number> | undefined,
  spellSlotsUsed: Record<number, number> | undefined,
  stageLabel?: string
): SlashCommandDefinition[] {
  if (isCantrip) {
    return [
      {
        name: 'Cantrip',
        description: 'No slot needed',
        isMenuOption: true,
        sourceId: '0',
        stageLabel,
        meta: { slotLevel: 0 },
      },
    ];
  }
  if (isFreeCast) {
    return [
      {
        name: `Innate (Level ${baseLevel})`,
        description: 'No slot needed',
        isMenuOption: true,
        sourceId: String(baseLevel),
        stageLabel,
        meta: { slotLevel: baseLevel, freeCast: true },
      },
    ];
  }
  const maxSlots = maxSpellSlots ?? {};
  const used = spellSlotsUsed ?? {};
  const options: SlashCommandDefinition[] = [];
  for (let lvl = baseLevel; lvl <= 9; lvl++) {
    const max = maxSlots[String(lvl)] ?? 0;
    if (max <= 0) continue;
    const remaining = max - (used[lvl] ?? 0);
    if (remaining <= 0) continue;
    const isBase = lvl === baseLevel;
    options.push({
      name: isBase ? `Level ${lvl} (base)` : `Level ${lvl}`,
      description: isBase
        ? `Cast normally — ${remaining}/${max} slots remaining`
        : `Upcast — ${remaining}/${max} slots remaining`,
      isMenuOption: true,
      sourceId: String(lvl),
      stageLabel,
      meta: { slotLevel: lvl },
    });
  }
  return options;
}

/**
 * Build damage-type options for the `transmuted_damage_type` step. SRD 5.2
 * Transmuted Spell metamagic: change a spell's damage type to one of Acid,
 * Cold, Fire, Lightning, Poison, or Thunder.
 *
 * `excludeType` hides the spell's natural damage type from the list — the
 * picker step runs AFTER the spell pick, so the dispatcher can read the
 * picked spell's `effect.damage_type` and pass it through. Without this
 * filter, the player could spend 1 Sorcery Point to "transmute" Fire →
 * Fire on Burning Hands — pure no-op SP burn (SRD allows any of the 6
 * types, including the original, but it's clearly never the intent).
 */
export function buildTransmutedDamageTypeOptions(
  stageLabel?: string,
  excludeType?: DamageType
): SlashCommandDefinition[] {
  const types: { id: DamageType; label: string; emoji: string }[] = [
    { id: 'acid', label: 'Acid', emoji: '🧪' },
    { id: 'cold', label: 'Cold', emoji: '❄️' },
    { id: 'fire', label: 'Fire', emoji: '🔥' },
    { id: 'lightning', label: 'Lightning', emoji: '⚡' },
    { id: 'poison', label: 'Poison', emoji: '☠️' },
    { id: 'thunder', label: 'Thunder', emoji: '💥' },
  ];
  return types
    .filter(({ id }) => id !== excludeType)
    .map(({ id, label, emoji }) => ({
      name: `${emoji} ${label}`,
      description: `Reshape the spell's damage to ${label}`,
      isMenuOption: true,
      sourceId: id,
      stageLabel,
      meta: { transmutedDamageType: id },
    }));
}

export function buildTravelDestinationOptions(
  destinations: TravelDestination[] | undefined,
  stageLabel?: string
): SlashCommandDefinition[] {
  if (!destinations?.length) return [];
  // Dedupe by display name — when the same destination is reachable via
  // multiple edges/paths (e.g., Shepherds' Meadow appearing twice with
  // different travel times), keep ONLY the shortest-travel-time entry so
  // the menu lists each unique destination exactly once. Lower minutes
  // wins; missing minutes treated as Infinity (slowest). On a tie, prefer
  // the lexicographically smaller `id` so the surviving exitId is stable
  // regardless of server ordering.
  const shortestByName = new Map<string, TravelDestination>();
  for (const dest of destinations) {
    const existing = shortestByName.get(dest.name);
    const candidateMinutes = dest.travelMinutes ?? Number.POSITIVE_INFINITY;
    const existingMinutes = existing?.travelMinutes ?? Number.POSITIVE_INFINITY;
    const replace =
      !existing ||
      candidateMinutes < existingMinutes ||
      (candidateMinutes === existingMinutes && dest.id < existing.id);
    if (replace) {
      shortestByName.set(dest.name, dest);
    }
  }
  return Array.from(shortestByName.values()).map((dest) => {
    let description: string;
    if (dest.destinationType === 'scene') {
      const parts: string[] = [];
      if (dest.direction) parts.push(dest.direction);
      if (dest.travelMinutes) parts.push(`${dest.travelMinutes} min`);
      description = parts.length ? parts.join(', ') : 'Nearby';
    } else {
      const hours = dest.travelMinutes ? Math.round(dest.travelMinutes / 60) : 0;
      description = hours ? `${hours}h travel` : 'Travel';
    }
    return {
      name: dest.name,
      description,
      isMenuOption: true,
      sourceId: dest.id,
      stageLabel,
      meta: { travelType: dest.destinationType, exitId: dest.id },
    };
  });
}
